import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header2";
import OrderCard from "./components/OrderCard";
import OrderTable from "./components/OrderTable";
import OrderFilters from "./components/OrderFilters";
import OrderDetailModal from "./components/OrderDetailModal";
import Pagination from "./components/Pagination";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  cancelOrderService,
  getOrderService,
} from "redux/services/general_service";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";

const OrderHistory = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { ordersList, metaOrder,isLoading } = useSelector((state) => state.general);
  const [viewMode, setViewMode] = useState("cards"); // 'cards' or 'table'
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    timeRange: "all",
    sortBy: "date-desc",
  });

  const itemsPerPage = 10;

  // Mock orders data
  const mockOrders = [
    {
      id: "ORD-2025-001",
      orderNumber: "2025-001",
      date: "2025-01-15T10:30:00Z",
      status: "Delivered",
      total: 299.99,
      subtotal: 259.99,
      shipping: 15.0,
      tax: 25.0,
      itemCount: 3,
      primaryImage:
        "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400",
      primaryProduct: "Wireless Headphones",
      trackingNumber: "TRK123456789",
      carrier: "FedEx",
      items: [
        {
          name: "Wireless Bluetooth Headphones",
          image:
            "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400",
          price: 149.99,
          quantity: 1,
          variant: "Black",
        },
        {
          name: "Phone Case",
          image:
            "https://images.unsplash.com/photo-1601593346740-925612772716?w=400",
          price: 29.99,
          quantity: 2,
          variant: "Clear",
        },
        {
          name: "USB Cable",
          image:
            "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400",
          price: 19.99,
          quantity: 1,
          variant: "6ft",
        },
      ],
      shippingAddress: {
        name: "John Smith",
        street: "123 Main Street",
        city: "New York",
        state: "NY",
        zip: "10001",
        country: "United States",
      },
      paymentMethod: {
        type: "Visa",
        last4: "4242",
      },
    },
    // ... other mock orders
  ];

  // Filter and sort orders
  const getFilteredOrders = () => {
    let filtered = [...mockOrders];

    // Search filter
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.orderNumber.toLowerCase().includes(searchTerm) ||
          order.primaryProduct.toLowerCase().includes(searchTerm) ||
          order.items.some((item) =>
            item.name.toLowerCase().includes(searchTerm)
          )
      );
    }

    // Status filter
    if (filters.status !== "all") {
      filtered = filtered.filter(
        (order) => order.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    // Time range filter
    if (filters.timeRange !== "all") {
      const now = new Date();
      const filterDate = new Date();

      switch (filters.timeRange) {
        case "30days":
          filterDate.setDate(now.getDate() - 30);
          break;
        case "90days":
          filterDate.setDate(now.getDate() - 90);
          break;
        case "1year":
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          filterDate = null;
      }

      if (filterDate) {
        filtered = filtered.filter(
          (order) => new Date(order.date) >= filterDate
        );
      }
    }

    // Sort orders
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case "date-desc":
          return new Date(b.date) - new Date(a.date);
        case "date-asc":
          return new Date(a.date) - new Date(b.date);
        case "amount-desc":
          return b.total - a.total;
        case "amount-asc":
          return a.total - b.total;
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

    return filtered;
  };

  const filteredOrders = getFilteredOrders();
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      status: "all",
      timeRange: "all",
      sortBy: "date-desc",
    });
    setCurrentPage(1);
  };

  const handleViewDetails = (orderId) => {
    const order = mockOrders.find((o) => o.id === orderId);
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleTrackOrder = (orderId) => {
    // Mock tracking functionality
    alert(
      `Tracking order ${orderId}. In a real app, this would open tracking details.`
    );
  };

  const handleReorder = (orderId) => {
    const order = mockOrders.find((o) => o.id === orderId);
    if (order) {
      // Mock reorder functionality - add items to cart
      alert(
        `Added ${order.itemCount} items from order ${order.orderNumber} to cart.`
      );
      navigate("/shopping-cart");
    }
  };

  const handleDownloadInvoice = (orderId) => {
    // Mock invoice download
    alert(
      `Downloading invoice for order ${orderId}. In a real app, this would download a PDF.`
    );
  };

  const handleReturnItems = (orderId) => {
    // Mock return functionality
    alert(
      `Initiating return for order ${orderId}. In a real app, this would open the return process.`
    );
  };

  const handleCancelOrder = (orderId) => {
    dispatch(
      cancelOrderService({ id: orderId, status: "canceled", _method: "PUT" })
    ).then((response) => {
      if (
        response?.payload?.status == 200 ||
        response?.payload?.status == 204
      ) {
        toast("canceleed");
        dispatch(getOrderService({ page: 1 }));
      }
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    dispatch(getOrderService({ page: 1 }));
  }, [filters]);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lg:py-24 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {t("orderHistory")}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t("trackAndManageYourOrders")}
              </p>
            </div>
          </div>
        </div>

        {/* Filters 
          <OrderFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            totalOrders={mockOrders.length}
            filteredCount={filteredOrders.length}
          />*/}

        {/* Orders Display */}
        {paginatedOrders.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Icon
              name="Package"
              size={64}
              className="mx-auto text-muted-foreground mb-4"
            />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {t("noOrdersFound")}
            </h3>
            <p className="text-muted-foreground mb-6">
              {filters.search ||
              filters.status !== "all" ||
              filters.timeRange !== "all"
                ? t("noOrdersMatchFilters")
                : t("noOrdersYet")}
            </p>
            {!filters.search &&
              filters.status === "all" &&
              filters.timeRange === "all" && (
                <Button
                  variant="default"
                  iconName="ShoppingBag"
                  iconPosition="left"
                  onClick={() => navigate("/")}
                >
                  {t("startShopping")}
                </Button>
              )}
          </div>
        ) : (
          <>
        
            {viewMode === "cards" ? (
              <div className="space-y-4">
               {isLoading && (
  <Skeleton
    count={4}
    className="bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow mb-4 h-40"
  />
)}

                {ordersList?.length === 0 && !isLoading ? (
                  <div className="flex flex-col items-center justify-center">
                    <img
                      src="/assets/images/no_order.jpg"
                      alt="logo"
                      className="w-80 h-80 object-cover rounded-lg"
                    />
                    <p
                      className="mt-4 text-lg text-gray-700 font-semibold tracking-wide text-center leading-relaxed"
                      style={{ fontFamily: "'Cairo', sans-serif" }}
                    >
                      {t("no_orders")}
                    </p>
                  </div>
                ) : (
                  ordersList?.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      onViewDetails={handleViewDetails}
                      onTrackOrder={handleTrackOrder}
                      onReorder={handleReorder}
                      onDownloadInvoice={handleDownloadInvoice}
                      onReturnItems={handleReturnItems}
                      onCancelOrder={handleCancelOrder}
                    />
                  ))
                )}
              </div>
            ) : (
              <div className="hidden lg:block">
                <OrderTable
                  orders={paginatedOrders}
                  onViewDetails={handleViewDetails}
                  onTrackOrder={handleTrackOrder}
                  onReorder={handleReorder}
                  onDownloadInvoice={handleDownloadInvoice}
                  onReturnItems={handleReturnItems}
                />
              </div>
            )}

            {/* Mobile fallback for table view */}
            {viewMode === "table" && (
              <div className="lg:hidden space-y-4">
                {paginatedOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onViewDetails={handleViewDetails}
                    onTrackOrder={handleTrackOrder}
                    onReorder={handleReorder}
                    onDownloadInvoice={handleDownloadInvoice}
                    onReturnItems={handleReturnItems}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredOrders.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      {/* Order Detail Modal */}
      <OrderDetailModal
        order={selectedOrder}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTrackOrder={handleTrackOrder}
        onReorder={handleReorder}
        onDownloadInvoice={handleDownloadInvoice}
        onReturnItems={handleReturnItems}
      />
    </div>
  );
};

export default OrderHistory;
